
import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";

function Popover({
  open,
  onOpenChange,
  ...props
}: Readonly<React.ComponentProps<typeof PopoverPrimitive.Root>>) {
  React.useEffect(() => {
    if (!open) return;

    const hideAllAndClose = () => {
      onOpenChange?.(false);
    };

    // Case 1: browser tab switch — document.hidden becomes true
    const handleVisibilityChange = () => {
      if (document.hidden) hideAllAndClose();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Case 2: harness/shell tab switch — the iframe container is hidden via
    // display:none, which makes the body's intersection ratio drop to 0.
    // visibilitychange does NOT fire in this case.
    // hasBeenVisible guards against the initial callback firing with ratio=0
    // (which happens when the tab is still mid-transition back into view),
    // which would otherwise re-close the dropdown the user just clicked open.
    let intersectionObserver: IntersectionObserver | undefined;
    if (typeof IntersectionObserver !== 'undefined') {
      let hasBeenVisible = false;
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          const ratio = entries[0].intersectionRatio;
          if (ratio > 0) {
            hasBeenVisible = true;
          } else if (hasBeenVisible) {
            hideAllAndClose();
          }
        },
        { threshold: 0 },
      );
      intersectionObserver.observe(document.body);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      intersectionObserver?.disconnect();
    };
  }, [open, onOpenChange]);

  return (
    <PopoverPrimitive.Root
      data-slot="popover"
      open={open}
      onOpenChange={onOpenChange}
      {...props}
    />
  );
}

function PopoverTrigger({ ...props }: Readonly<React.ComponentProps<typeof PopoverPrimitive.Trigger>>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  ...props
}: Readonly<React.ComponentProps<typeof PopoverPrimitive.Content>>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "bg-popover data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden",
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({ ...props }: Readonly<React.ComponentProps<typeof PopoverPrimitive.Anchor>>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
